import { v4 as uuidv4 } from 'uuid';
import { IIntegerPrecisionService } from '@core/interfaces/integer-precision-service.interface';
import { IntegerPrecisionService } from '@core/services/integer-precision/integer-precision.service';
import {
	Order,
	OrderForm,
	OrderStatus,
	PaymentStatus,
	PaymentWay,
	OrderUpsertPayment,
	OrderDetailUpsert,
	OrderDetailTaxUpsert,
	OrderDetailCouponUpsert,
	MenuItem,
	Coupon,
	TipDetail,
	Store,
	WorkDay,
	OrderType,
	ClientLocation,
	Client,
	CartTotals,
	CouponItem,
	DeliveryZone,
	Tax,
	OrderUpsert,
	ModifierDetail,
	PaymentResult,
	ClientPersonalInformation,
	OrderDataPayload
} from '@core/models';
import {
	OrderPaymentStatusNameEnum,
	OrderTypeCodeEnum,
	OrderTypeEnum,
	TaxEnum,
	TipoValorEnum,
} from '@core/enums';
import { CartFactoryService, CartProcessorService } from '@/features/shoppingcart/domain';

export function transformToData(orden: Partial<Order>): Order {
	return orden as Order;
}

export function transformToForm(_orden: Order): OrderForm {
	return {} as OrderForm;
}

export function transformToUpsert(
	ordenForm: OrderForm,
	paymentStatus: PaymentStatus | null,
	paymentWey: PaymentWay | null
): OrderUpsert {
	const {
		storeData,
		localChannelId,
		cartTotals,
		cartList,
		deliveryZoneList,
		typeOrder,
		auth,
		userData,
		userLocation,
		workDay,
		statusOrder,
		coupon,
		tipDetail,
		paymentCard,
	} = ordenForm;

	const orderData = prepareOrderData(
		storeData,
		auth,
		localChannelId,
		cartTotals,
		userData,
		deliveryZoneList && deliveryZoneList.length > 0
			? deliveryZoneList[0]
			: null,
		typeOrder?.nombre
	);

	return prepareOrderPayload(
		storeData,
		orderData,
		localChannelId,
		workDay,
		statusOrder,
		paymentStatus,
		paymentWey,
		typeOrder,
		cartTotals,
		cartList,
		deliveryZoneList && deliveryZoneList.length > 0
			? deliveryZoneList[0]
			: null,
		userData?.apartmentOrSuite || null,
		userData?.deliveryInstructions || null,
		userLocation,
		coupon,
		tipDetail,
		paymentCard
	);
}

//Logica de negocio que se tendra que cambiar
function prepareOrderData(
	storeData: Store,
	user: Client | null,
	localCanalId: string,
	amount: CartTotals,
	clientPersonalInformation: ClientPersonalInformation,
	deliveryZone: DeliveryZone | null,
	orderType: string
): OrderDataPayload {
	const userFormData = {
		firstName: clientPersonalInformation?.firstName || '',
		lastName: clientPersonalInformation?.lastName || '',
		email: clientPersonalInformation?.email || '',
		phone: clientPersonalInformation?.phone || '',
	};

	const deliveryFee = amount.deliveryCharges ?? 0n;

	return {
		storeId: storeData.idLocal,
		deliveryFee: deliveryFee > 0n ? deliveryFee : 0n,
		deliveryZone:
			orderType == OrderTypeEnum.Delivery ? deliveryZone?.nombre : null,
		customerName: `${userFormData.firstName} ${userFormData.lastName}`,
		customerPhone: String(userFormData.phone),
		localCanalId,
		cienteId: user?.id || null,
		serviceType: orderType,
	};
}

function formatMoneyStr(val: bigint): string {
	return val.toString();
}

function calculateItemCouponDiscount(
	itemSubtotal: bigint,
	rawCouponValue: bigint
): bigint {
	return rawCouponValue > itemSubtotal ? itemSubtotal : rawCouponValue;
}

function calculateTaxableSubtotal(
	itemSubtotal: bigint,
	itemCouponDiscount: bigint
): bigint {
	const netSubtotal = itemSubtotal - itemCouponDiscount;
	return netSubtotal < 0n ? 0n : netSubtotal;
}

function mapItemTaxes(
	taxableSubtotal: bigint,
	itemQuantity: bigint,
	taxes: Tax[],
	precisionService: IIntegerPrecisionService
): OrderDetailTaxUpsert[] {
	return (taxes || []).map((impuesto) => {
		const valorImpuesto = BigInt(impuesto.valor ?? 0n);
		let valorCalculado = 0n;

		if (impuesto.esPorcentaje) {
			const isInclusive = impuesto.tipo === TaxEnum.Inclusive;
			const { tax } = precisionService.adjustTaxFromScaledValue(
				taxableSubtotal,
				impuesto.valor,
				isInclusive
			);
			valorCalculado = tax;
		} else {
			valorCalculado = valorImpuesto * itemQuantity;
		}

		return {
			nombre: impuesto.descripcion || 'TAX',
			impuestoId: impuesto.idImpuesto,
			valor: precisionService.toScaledStringVisual(valorImpuesto),
			esPorcentaje: impuesto.esPorcentaje,
			valorCalculado: precisionService.toScaledStringVisual(valorCalculado),
			tipo: impuesto.tipo,
		};
	});
}

function getCouponDiscountForItem(
	articuloId: string,
	coupon?: Coupon | null
): OrderDetailCouponUpsert | undefined {
	if (!coupon || !coupon.items) return undefined;

	const couponItem = coupon.items.find(
		(item: CouponItem) => item.id === articuloId
	);
	if (!couponItem) return undefined;

	return {
		nombre: coupon.nombre,
		tipo: coupon.tipo,
		cuponId: coupon.idCupon,
		valor: String(coupon.valor),
		esPorcentual: coupon.esPorcentual,
		valorCalculado: formatMoneyStr(BigInt(couponItem.valorCalculado || 0)),
	};
}

function prepareOrderPayload(
	storeData: Store,
	orderData: OrderDataPayload,
	localChannelId: string,
	diaLaboral: WorkDay,
	statusOrder: OrderStatus | null,
	paymentStatus: PaymentStatus | null,
	paymentWey: PaymentWay | null,
	typeOrder: OrderType,
	amount: CartTotals,
	orderItems: MenuItem[],
	deliveryZone: DeliveryZone | null,
	apartmentOrSuite: string | null,
	deliveryInstructions: string | null,
	location: ClientLocation | null,
	coupon: Coupon | null,
	tipDetail: TipDetail | null,
	paymentCard?: PaymentResult | null,
	integerPrecisionService: IIntegerPrecisionService = new IntegerPrecisionService()
): OrderUpsert {
	const filteredOrderItems =
		orderItems?.filter((item) => item?.storeSlug == storeData.slug) || [];
	const apartmentOrSuiteValue = apartmentOrSuite || '';
	const deliveryInstructionsValue = deliveryInstructions || '';
	const deliveryAddress = location?.address || '';

	const diaLaboralId = diaLaboral.idlaboral;
	const tiendaId = orderData.storeId;

	const detallesOrden = filteredOrderItems.map(
		(item: MenuItem, index: number) => {
			const precio = BigInt(item.precio ?? 0n);
			const precioBase = BigInt(item.precioBase ?? 0n);
			const itemCantidad = BigInt(item.cantidad ?? 1n);
			const scaledCantidad = Number(
				integerPrecisionService.changePrecisionToInternal(itemCantidad, 0)
			);

			const { menuItems: [menuItemWithSubtotal] } = new CartProcessorService(new CartFactoryService(), new IntegerPrecisionService()).calculateCartsWithSubtotalPrice([item]);
			const itemSubtotalConModificadores =
				BigInt(menuItemWithSubtotal?.price ?? 0n) * itemCantidad;

			return {
				detalleId: uuidv4(),
				posicionDetalle: index + 1,
				cantidad: scaledCantidad,
				articuloId: item.idArticulo,
				estadoId: statusOrder?.id || '',
				nombre: (item.nombre || 'Producto').substring(0, 100),
				nombreCocina: (item.nombre || 'Producto').substring(0, 100),
				nombreRecibo: (item.nombre || 'Producto').substring(0, 100),
				precio: integerPrecisionService.toScaledStringVisual(
					precio + precioBase
				),
				total: integerPrecisionService.toScaledStringVisual(
					itemSubtotalConModificadores
				),
				numeroCuenta: 0,
				esParaLlevar: typeOrder.codigo !== OrderTypeCodeEnum.Pickup,
				modificadores: (item.detailsWithQuantity || []).map(
					(mod: ModifierDetail) => {
						const modPrice = mod.isFree
							? 0n
							: BigInt(mod.precioBase ?? mod.precio ?? 0n);
						const extraQty = BigInt(mod.cantidad ?? 0n);
						const extraPrice =
							extraQty > 0n ? BigInt(mod.precio ?? 0n) * extraQty : 0n;
						const modTotal = modPrice + extraPrice;

						return {
							modificadorId:
								mod.idDetalleModificador ||
								(mod.modificador && mod.modificador.idModificador) ||
								mod.id ||
								'',
							nombre: (mod.nombre || 'Modificador').substring(0, 50),
							precio:
								integerPrecisionService.toScaledStringVisual(modPrice),
							total:
								integerPrecisionService.toScaledStringVisual(modTotal),
							esGratis: !!mod.isFree,
						};
					}
				),
			};
		}
	);

	const detallesCuenta: OrderDetailUpsert[] = filteredOrderItems.map(
		(item: MenuItem, index: number) => {
			const itemCantidad = BigInt(item.cantidad ?? 1n);
			const scaledCantidad = Number(
				integerPrecisionService.changePrecisionToInternal(itemCantidad, 0)
			);

			const { menuItems: [menuItemWithSubtotal] } = new CartProcessorService(new CartFactoryService(), new IntegerPrecisionService()).calculateCartsWithSubtotalPrice([item]);
			const subtotal =
				BigInt(menuItemWithSubtotal?.price ?? 0n) * itemCantidad;

			const rawCouponValue = coupon?.items?.find(
				(i: CouponItem) => i.id === item.idArticulo
			)?.valorCalculado
				? BigInt(
						coupon.items.find(
							(i: CouponItem) => i.id === item.idArticulo
						)!.valorCalculado || 0
					)
				: 0n;

			const itemCouponDiscount = calculateItemCouponDiscount(
				subtotal,
				rawCouponValue
			);
			const taxableSubtotal = calculateTaxableSubtotal(
				subtotal,
				itemCouponDiscount
			);
			const totalItemNet = taxableSubtotal;

			const cuponMapped = getCouponDiscountForItem(
				item.idArticulo,
				coupon
			);
			const impuestosItem = mapItemTaxes(
				taxableSubtotal,
				itemCantidad,
				item.impuestos,
				integerPrecisionService
			);

			return {
				detalleId: detallesOrden[index].detalleId,
				cantidad: scaledCantidad,
				subtotal: integerPrecisionService.toScaledStringVisual(subtotal),
				total: integerPrecisionService.toScaledStringVisual(totalItemNet),
				articuloId: item.idArticulo,
				cupon: cuponMapped,
				posicionDetalle: index + 1,
				estado: {
					estadoId: statusOrder?.id || '',
					nombre: statusOrder?.nombre || '',
				},
				impuestos: impuestosItem,
				numeroCuenta: 0,
				uniqueId: uuidv4(),
				libreImpuestos: false,
			};
		}
	);

	const totalGeneralOrden = amount.total;
	const totalConsumoBase =
		amount.subtotal +
		(typeOrder?.nombre === OrderTypeEnum.Delivery
			? (amount.deliveryCharges ?? 0n)
			: 0n) +
		amount.taxes -
		amount.couponDiscount;

	const selectedZone = Array.isArray(deliveryZone)
		? deliveryZone[0]
		: deliveryZone;

	const storeTimeZone = storeData.ubicacion?.zonaHoraria || 'UTC';
	const dtfFecha = new Intl.DateTimeFormat('en-US', {
		timeZone: storeTimeZone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	});
	const partsFecha = dtfFecha.formatToParts(new Date());
	const yearVal =
		partsFecha.find((p) => p.type === 'year')?.value || '1970';
	const monthVal =
		partsFecha.find((p) => p.type === 'month')?.value || '01';
	const dayVal = partsFecha.find((p) => p.type === 'day')?.value || '01';
	const fecha = `${yearVal}-${monthVal}-${dayVal}`;

	const dtfHora = new Intl.DateTimeFormat('en-US', {
		timeZone: storeTimeZone,
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});
	const partsHora = dtfHora.formatToParts(new Date());
	const hourVal = partsHora.find((p) => p.type === 'hour')?.value || '00';
	const minuteVal =
		partsHora.find((p) => p.type === 'minute')?.value || '00';
	const hora = `${hourVal}:${minuteVal}`;

	const paymentId = paymentCard?.idPayment;

	const rawTipAmount = tipDetail?.value ?? tipDetail?.valor;
	const tipInternalVal =
		tipDetail && rawTipAmount != null
			? integerPrecisionService.toInternalFromDecimal(rawTipAmount)
			: 0n;
	const hasTip = tipInternalVal > 0n;
	const propinaValorStr = hasTip
		? integerPrecisionService.toScaledStringVisual(tipInternalVal)
		: '0';

	const pagos: OrderUpsertPayment[] =
		statusOrder?.nombre === OrderPaymentStatusNameEnum.Closed && paymentWey && paymentStatus
			? [
					{
						total:
							integerPrecisionService.toScaledStringVisual(
								totalConsumoBase
							),
						formaPagoId: paymentWey.idFormaPago || paymentWey.idFormaPago || '',
						estadoId: paymentStatus.idEstado || paymentStatus.idEstado || '',
						paymentId: paymentId || '',
						...(hasTip
							? {
									propina: {
										descripcion: tipDetail?.descripcion || 'Propina',
										valor: propinaValorStr,
										...(tipDetail?.idDetallePropina
											? { propinaId: tipDetail.idDetallePropina }
											: {}),
									},
								}
							: {}),
					} as unknown as OrderUpsertPayment,
			  ]
			: [];

	const cuentas = [
		{
			fecha,
			hora,
			accountId: uuidv4(),
			diaLaboralId,
			tiendaId: tiendaId || '',
			clienteId: orderData?.cienteId,
			subtotal: integerPrecisionService.toScaledStringVisual(
				amount.subtotal
			),
			impuesto: integerPrecisionService.toScaledStringVisual(amount.taxes),
			descuento: '0',
			cupon: integerPrecisionService.toScaledStringVisual(
				amount.couponDiscount
			),
			total:
				integerPrecisionService.toScaledStringVisual(totalConsumoBase),
			detalles: detallesCuenta,
			estado: {
				estadoId: statusOrder?.id || '',
			},
			precisionDecimal: integerPrecisionService.visualPrecision,
			pagos,
			libreImpuestos: false,
			...(typeOrder?.nombre === OrderTypeEnum.Delivery && selectedZone
				? {
						zonaEntrega: {
							zonaEntregaId: selectedZone.idZonaEntrega,
							nombre: selectedZone.nombre,
							tarifa: integerPrecisionService.toScaledStringVisual(
								amount.deliveryCharges ?? 0n
							),
							tipoTarifa: selectedZone.tipoTarifa || TipoValorEnum.Dolar,
							valorCompensacion:
								integerPrecisionService.toScaledStringVisual(
									String(selectedZone.valorCompensacion ?? 0)
								),
							tipoValorCompensacion:
								selectedZone.tipoValorCompensacion || TipoValorEnum.Dolar,
						},
						deliveryInfo: {
							clienteNombre: orderData?.customerName || 'Cliente',
							telefono: orderData?.customerPhone || '0000000000',
							direccion: deliveryAddress || 'N/A',
							instruccion:
								apartmentOrSuiteValue && apartmentOrSuiteValue.trim()
									? apartmentOrSuiteValue.trim()
									: deliveryAddress || 'N/A',
							nota: deliveryInstructionsValue || '',
						},
					}
				: {}),
		},
	];

	return {
		orden: {
			orderId: uuidv4(),
			codigo: '1',
			nombreInvitado: (orderData?.customerName || 'Cliente').substring(
				0,
				20
			),
			total:
				integerPrecisionService.toScaledStringVisual(totalGeneralOrden),
			fecha,
			hora,
			invitados: 1,
			estacionId: localChannelId,
			tiendaId: tiendaId || '',
			diaLaboralId,
			estadoId: statusOrder?.id || '',
			precisionDecimal: integerPrecisionService.visualPrecision,
			tipoOrden: {
				tipoOrdenId: typeOrder.idTipoOrden,
				nombre: typeOrder.nombre,
			},
			detalles: detallesOrden,
		},
		divisiones: 1,
		cuentas,
	};
}
